import { FC } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { FramerTooltip, InputSearch } from "@ryde/controls";
import {
  AUDIT_LOG_CHANGE_FILTER_LIST,
  DATA_E2E_KEY,
  TAuditLogChangeFilter,
  TAuditLogExportScope,
} from "@ryde/shared";

import { SearchSectionExport } from "./search-section-export";
import { SearchSectionStyled } from "./search-section.styled";

interface ISearchSectionProps {
  onSearchType: (searchValue: string) => void;
  onSearchApply: (searchValue: string) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  searchValue?: string;
  keySearchValue?: string;
  onKeySearchType?: (keySearchValue: string) => void;
  showSearchResultsOnly?: boolean;
  onToggleSearchResultsOnly?: (checked: boolean) => void;
  onRefresh?: () => void;
  changeFilters?: TAuditLogChangeFilter[];
  changeFilterCounts?: Record<TAuditLogChangeFilter, number>;
  onToggleChangeFilter?: (filter: TAuditLogChangeFilter) => void;
  shownCount?: number;
  totalCount?: number;
  onExport?: (scope: TAuditLogExportScope) => void;
}

export const SearchSection: FC<ISearchSectionProps> = ({
  onSearchType,
  onSearchApply,
  onExpandAll,
  onCollapseAll,
  searchValue,
  keySearchValue,
  onKeySearchType,
  showSearchResultsOnly,
  onToggleSearchResultsOnly,
  onRefresh,
  changeFilters = [],
  changeFilterCounts,
  onToggleChangeFilter,
  shownCount,
  totalCount,
  onExport,
}) => {
  const { t } = useTranslation();

  return (
    <SearchSectionStyled>
      <div className="search-section__content">
        <div className="search-section__search">
          <InputSearch
            label={t("auditLog.search.value")}
            onSearchType={onSearchType}
            onSearchApply={onSearchApply}
            value={searchValue}
          />
        </div>
        {onKeySearchType && (
          <div className="search-section__search search-section__search--key">
            <InputSearch
              label={t("auditLog.search.key")}
              onSearchType={onKeySearchType}
              onSearchApply={onKeySearchType}
              value={keySearchValue}
            />
          </div>
        )}
        <div className="search-section__actions">
          {onExpandAll && (
            <FramerTooltip content={t("general.EXPAND_ALL")}>
              <button
                type="button"
                className="search-section__action-button"
                onClick={onExpandAll}
                {...{ [DATA_E2E_KEY]: "search-section-expand-all-button" }}
              >
                <SVG_MUI.ArrowStatExpand3SVG />
              </button>
            </FramerTooltip>
          )}
          {onCollapseAll && (
            <FramerTooltip content={t("general.COLLAPSE_ALL")}>
              <button
                type="button"
                className="search-section__action-button"
                onClick={onCollapseAll}
                {...{ [DATA_E2E_KEY]: "search-section-collapse-all-button" }}
              >
                <SVG_MUI.ArrowStatCollapse3SVG />
              </button>
            </FramerTooltip>
          )}
          {onRefresh && (
            <FramerTooltip content={t("general.REFRESH")}>
              <button
                type="button"
                className="search-section__action-button"
                onClick={onRefresh}
                {...{ [DATA_E2E_KEY]: "search-section-refresh-button" }}
              >
                <SVG_MUI.ArrowsRotate3SVG />
              </button>
            </FramerTooltip>
          )}
        </div>
      </div>
      {(onToggleChangeFilter || onExport) && (
        <div className="search-section__toolbar">
          {onToggleChangeFilter && (
            <div
              className="search-section__filters"
              role="group"
              aria-label={t("auditLog.changes.filterLabel")}
            >
              {AUDIT_LOG_CHANGE_FILTER_LIST.map(filter => {
                const count = changeFilterCounts?.[filter];
                const isPressed = changeFilters.includes(filter);
                return (
                  <button
                    key={filter}
                    type="button"
                    className={`search-section__filter search-section__filter--${filter}${count === 0 && !isPressed ? " search-section__filter--empty" : ""}`}
                    aria-pressed={isPressed}
                    onClick={() => onToggleChangeFilter(filter)}
                    {...{ [DATA_E2E_KEY]: `search-section-filter-${filter}` }}
                  >
                    {t(`auditLog.changes.filter.${filter}`)}
                    {count !== undefined && (
                      <span className="search-section__filter-count">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="search-section__summary">
            {shownCount !== undefined && totalCount !== undefined && (
              <span
                className="search-section__counter"
                aria-live="polite"
                {...{ [DATA_E2E_KEY]: "search-section-counter" }}
              >
                {t("auditLog.count.shown", {
                  shown: shownCount,
                  total: totalCount,
                })}
              </span>
            )}
            {onExport && totalCount !== undefined && (
              <SearchSectionExport
                allCount={totalCount}
                filteredCount={shownCount ?? totalCount}
                onExport={onExport}
              />
            )}
          </div>
        </div>
      )}
      {onToggleSearchResultsOnly && (
        <div className="search-section__checkbox">
          <label className="search-section__checkbox-label">
            <input
              type="checkbox"
              checked={showSearchResultsOnly || false}
              onChange={e => onToggleSearchResultsOnly(e.target.checked)}
              className="search-section__checkbox-input"
              {...{ [DATA_E2E_KEY]: "search-results-only-checkbox" }}
            />
            <span className="search-section__checkbox-text">
              {t("general.SEARCH_RESULTS_ONLY")}
            </span>
          </label>
          {onRefresh && (
            <div className="search-section__footer-buttons">
              {onRefresh && (
                <button
                  type="button"
                  className="search-section__footer-button"
                  onClick={onRefresh}
                  title={t("general.REFRESH")}
                  {...{ [DATA_E2E_KEY]: "search-section-refresh-button" }}
                >
                  <SVG_MUI.ArrowsRotate3SVG />
                  {t("general.REFRESH")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </SearchSectionStyled>
  );
};
